import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Building2, Target, Upload, CheckCircle2, Clock, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { conversionAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [converting, setConverting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF file',
        variant: 'destructive'
      });
      return;
    }

    setConverting(true);
    try {
      const uploadResult = await conversionAPI.uploadPDF(file, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${progress}%`);
      });

      // Poll for conversion status
      const conversionId = uploadResult.conversionId;
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds max

      const checkStatus = async () => {
        const status = await conversionAPI.getStatus(conversionId);
        
        if (status.status === 'completed') {
          setConverting(false);
          toast({
            title: 'Conversion successful!',
            description: `${file.name} has been converted to Excel format.`,
            action: (
              <a 
                href={conversionAPI.downloadFile(conversionId)}
                download
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-pink-500 text-white hover:bg-pink-600 h-10 px-4 py-2"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            ),
          });
        } else if (status.status === 'failed') {
          setConverting(false);
          toast({
            title: 'Conversion failed',
            description: status.error || 'Please try again or contact support.',
            variant: 'destructive'
          });
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 1000);
        } else {
          setConverting(false);
          toast({
            title: 'Conversion timeout',
            description: 'The conversion is taking longer than expected. Please check your dashboard.',
            variant: 'destructive'
          });
        }
      };

      setTimeout(checkStatus, 1000);

    } catch (error) {
      setConverting(false);
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Please try again or contact support.',
        variant: 'destructive'
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleButtonClick = () => {
    document.getElementById('fileInput').click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-pink-100 to-pink-50">
      {/* Hero Section */}
      <section className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              Transform your financial documents<br />in seconds
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Convert PDF bank statements from thousands of banks globally into organized Excel spreadsheets instantly.
            </p>
          </div>

          {/* Upload Area */}
          <div className="max-w-3xl mx-auto">
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl p-16 border-2 border-dashed transition-all ${
                dragActive ? 'border-pink-500 bg-pink-200' : 'border-pink-300'
              }`}
            >
              <div className="text-center">
                <Button
                  onClick={handleButtonClick}
                  disabled={converting}
                  size="lg"
                  className="bg-gradient-to-r from-pink-400 to-pink-300 hover:from-pink-500 hover:to-pink-400 text-white text-lg px-12 py-6 h-auto shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  {converting ? (
                    <>
                      <Clock className="w-5 h-5 mr-2 animate-spin" />
                      Converting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Click here to convert a PDF!
                    </>
                  )}
                </Button>
                <input
                  id="fileInput"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                />
                <p className="mt-4 text-sm text-gray-600">
                  or drag and drop your PDF file here
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-br from-pink-100 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center bg-white/50 border-pink-200 hover:shadow-xl transition-shadow backdrop-blur-sm">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-pink-300 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Secure</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-700">
                  With years of experience in banking we comply with strict standards when handling your files.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center bg-white/50 border-pink-200 hover:shadow-xl transition-shadow backdrop-blur-sm">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-pink-300 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Institutional</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-700">
                  We've provided our services to thousands of reputable financial, accounting and legal firms.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center bg-white/50 border-pink-200 hover:shadow-xl transition-shadow backdrop-blur-sm">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-pink-300 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Accurate</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-700">
                  We're continually improving our algorithms. If a file doesn't convert to your expectations, email us and we'll fix it.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-b from-pink-50 to-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-pink-200">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Upload PDF</h3>
              <p className="text-gray-700">
                Select or drag your bank statement PDF file
              </p>
            </div>
            <div className="text-center bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-pink-200">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Auto Convert</h3>
              <p className="text-gray-700">
                Our AI extracts and formats the data automatically
              </p>
            </div>
            <div className="text-center bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-pink-200">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-lg">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Download Excel</h3>
              <p className="text-gray-700">
                Get your clean Excel file ready to use
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-pink-100 via-pink-200 to-pink-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Need more conversions?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Subscribe to our premium plan for unlimited conversions and priority support.
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            size="lg"
            className="bg-gradient-to-r from-pink-500 to-pink-400 hover:from-pink-600 hover:to-pink-500 text-white text-lg px-8 py-6 h-auto shadow-xl hover:shadow-2xl transition-all"
          >
            View Pricing
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;